import { forwardRef, useRef, useImperativeHandle } from 'react';
import { X } from 'lucide-react';
import { Input } from './input';

/**
 * ClearableInput — wraps the shadcn Input with:
 *  - explicit text cursor (cursor-text)
 *  - small (×) clear-button that appears when the field has content
 *
 * Drop-in replacement for <Input>. Passes through all props.
 *
 * Usage:
 *   <ClearableInput value={x} onChange={(e) => setX(e.target.value)} />
 *
 * Accepts an optional onClear handler — defaults to firing onChange with "".
 */
const ClearableInput = forwardRef(function ClearableInput(
    { value = '', onChange, onClear, className = '', clearTestId, ...rest },
    ref,
) {
    const innerRef = useRef(null);
    useImperativeHandle(ref, () => innerRef.current);

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onClear) {
            onClear();
        } else if (onChange) {
            // Synthesize a change event so callers using onChange(e) keep working
            onChange({ target: { value: '' } });
        }
        // Refocus so user can keep typing
        if (innerRef.current && typeof innerRef.current.focus === 'function') {
            innerRef.current.focus();
        }
    };

    const hasValue = value != null && String(value).length > 0;

    return (
        <div className="relative flex-1">
            <Input
                ref={innerRef}
                value={value ?? ''}
                onChange={onChange}
                className={`cursor-text ${hasValue ? 'pr-9' : ''} ${className}`}
                {...rest}
            />
            {hasValue && (
                <button
                    type="button"
                    onMouseDown={handleClear}
                    aria-label="Clear input"
                    data-testid={clearTestId || 'input-clear-btn'}
                    className="absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    tabIndex={-1}
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
});

export { ClearableInput };
export default ClearableInput;
